import { useEffect, useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  TextField,
  Button,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import {
  getCitizenRequests,
  updateCitizenRequestStatus,
} from "../config/api/citizenRequestApi";
import AdminLayout from "../layouts/adminLayout/AdminLayout";
import CitizenRequestDetailsModal from "../components/CitizenRequestModal";
import { formatDate } from "../utils/date";

export default function CitizenRequestList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [requestType, setRequestType] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await getCitizenRequests({
        page: page + 1,
        per_page: pageSize,
        request_type: requestType || undefined,
        status: status || undefined,
        search: search || undefined,
      });

      setRows(res.data.data || []);
      setRowCount(res.data.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, requestType, status]);

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateCitizenRequestStatus(id, {
        _method: "PATCH",
        status: newStatus,
      });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update status");
    }
  };

  const handleOpenModal = (row) => {
    setSelectedData(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedData(null);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "tracking_no",
      headerName: "Tracking No",
      width: 180,
    },
    {
      field: "request_type",
      headerName: "Type",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "complaint" ? "error" : "primary"}
          size="small"
        />
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 180,
    },
    {
      field: "mobile",
      headerName: "Mobile",
      width: 140,
    },
    {
      field: "division_name",
      headerName: "Division",
      width: 130,
    },
    {
      field: "district_name",
      headerName: "District",
      width: 130,
    },
    {
      field: "subject",
      headerName: "Subject",
      width: 180,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "appointment_date",
      headerName: "Appointment Date",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => (
        <Select
          size="small"
          value={params.value || "pending"}
          onChange={(e) => handleStatusChange(params.row.id, e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
        </Select>
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 180,
       renderCell: (params) => formatDate(params?.row?.created_at)
    },
    {
      field: "action",
      headerName: "Action",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOpenModal(params.row)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout title={"Citizen Request"}>
      <Box className="p-6 bg-white !rounded">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select
            size="small"
            displayEmpty
            value={requestType}
            onChange={(e) => setRequestType(e.target.value)}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="complaint">Complaint</MenuItem>
            <MenuItem value="appointment">Appointment</MenuItem>
          </Select>

          <Select
            size="small"
            displayEmpty
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>

          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
        </div>

        <div style={{ height: 650, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => handleOpenModal(params.row)}
          />
        </div>

        <CitizenRequestDetailsModal
          open={openModal}
          onClose={handleCloseModal}
          data={selectedData}
        />
      </Box>
    </AdminLayout>
  );
}